import { AxiosError } from "axios";
import {
  Button,
  Checkbox,
  Container,
  DateInput,
  Form,
  Input,
  Message,
  Select,
  Stack,
  Textarea,
  useBasisForm,
} from "basis";
import { getIsProduction } from "library/browser";
import {
  validateAuMobileNumber,
  validateEmail,
  validateName,
  validateNzMobileNumber,
} from "library/formValidation";
import { createCloseAccountTicket } from "library/zendeskAPI";
import { storeRequest } from "library/zendeskAPI/zendeskUtils";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { reasonOptions } from "./options";
import { CloseAccountParams, FormValues } from "./types";
import { validateOver18YearsOld } from "./utils";

function CloseAccountForm({ state, dispatch }) {
  const { accountInfo } = state;
  const { methods, Field } = useBasisForm<FormValues>({
    defaultValues: state.formValues,
  });
  const { watch } = methods;
  const createTicketMutation = useMutation<
    number,
    AxiosError,
    CloseAccountParams
  >(createCloseAccountTicket, {
    onSuccess: (
      ticketId,
      {
        email,
        firstName,
        lastName,
        dateOfBirth,
        mobileNumber,
        confirmationLetter,
      },
    ) => {
      storeRequest({
        userEmail: accountInfo.userEmail,
        accountNumber: accountInfo.accountNumber,
        requestType: "closeAccount",
        ticketId: String(ticketId),
      });

      dispatch({
        type: "CLOSE_ACCOUNT_SUCCEEDED",
        formValues: {
          email,
          firstName,
          lastName,
          dateOfBirth,
          mobileNumber,
          confirmationLetter,
        },
      });
    },
  });
  const onSubmit = (values: FormValues) => {
    createTicketMutation.mutate({
      isProduction: getIsProduction(),
      userName: accountInfo.userName,
      reason: values.reason,
      otherInfo: values.reason === "Other " ? values.otherInfo : "",
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      mobileNumber: values.mobileNumber,
      last4digits: accountInfo.accountNumber.slice(-4),
      confirmationLetter: values.confirmationLetter,
      isNZ: accountInfo.isNZ,
      token: accountInfo.cardToken,
      productName: accountInfo.cardName,
    });
  };

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack gap="8">
        <Container bg="grey.t05" padding="4">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
          To help us improve our service, please let us know why you're closing
          your account today, then confirm some basic details, and we'll process
          your account closure.
        </Message>
        <Container width-sm="400">
          <Field
            as={Select}
            name="reason"
            label="Tell us why you're closing your account"
            placeholder="Select a reason"
            options={reasonOptions}
          />
        </Container>
        {watch("reason") === "Other " && (
          <Field
            as={Textarea}
            name="otherInfo"
            label="Your feedback matters. Tell us a little more about why you're closing your account."
            maxLength="100"
            helpText="Maximum 100 characters"
          />
        )}
        <Stack gap="8" width-sm="400">
          <Field
            as={Input}
            name="email"
            label="Email address"
            validate={validateEmail}
            disabled
          />
          <Field
            as={Input}
            name="firstName"
            label="First name"
            validate={validateName}
          />
          <Field
            as={Input}
            name="lastName"
            label="Last name"
            validate={validateName}
          />
          <Field
            as={DateInput}
            name="dateOfBirth"
            label="Date of birth"
            validate={validateOver18YearsOld}
          />
          <Field
            as={Input}
            type="tel"
            name="mobileNumber"
            label="Mobile number"
            validate={
              accountInfo.isNZ ? validateNzMobileNumber : validateAuMobileNumber
            }
          />
        </Stack>
        <Field
          as={Checkbox}
          name="confirmationLetter"
          label="Would you like a confirmation letter?"
          hideLabel
          optional
        >
          Provide me with a letter confirming my account closure
        </Field>
        {createTicketMutation.isError && (
          <Message severity="blocking" bg="secondary.pink.t30">
            Sorry, there was an error creating your close account request.
            Please try again later.
          </Message>
        )}
        <Button
          type="submit"
          width="100%"
          width-xs="256"
          loading={createTicketMutation.isLoading}
        >
          Close my account
        </Button>
      </Stack>
    </Form>
  );
}

export { CloseAccountForm };
